/**
 * @file BuilderStatusPills.tsx
 * @description Два статуса в шапке конструктора (phase19.16 Р1-6а; Р3а —
 * uiux-r3 §3.1, §4 «Статус в шапке»): «Вид сайта: N изменений ›» (открывает
 * «Что изменено») или «Вид сайта опубликован»; «Страница: 3 правки ›» /
 * «Страница: ещё не была на сайте» / «Страница скрыта» / «Страница на
 * сайте» — открывает панель «Публикации» (на ПК — группа справа, до 1280 —
 * шторка). У места — «Место: …»; на экранах «Блог»/«Каталог» — «Места: N
 * правок ›» по черновикам мест (открывает окно публикации); у прочих
 * экранов и шаблона товара второго статуса нет.
 */

import { SITE_SLOTS } from "@chaiberi/shared";
import { Check, ChevronRight } from "lucide-react";

import { StatusPill } from "../../ui/StatusPill";
import { hasDraftChanges, useSiteView } from "../../site-view/site-view-context";
import { useBuilder } from "../builder-context";
import { pluralRu } from "../../../lib/plural";
import { pagePillOf, slotsPillOf } from "../../../lib/site-builder/page-status";

/**
 * В шапке на 1024–1279 подпись статуса — только для скринридера (значок и
 * число видны, QA SB120 3б); до 1024 статусы строкой содержимого — полностью.
 */
const NARROW_HIDDEN = "lg:max-xl:sr-only";

export function BuilderStatusPills() {
  const view = useSiteView();
  const b = useBuilder();
  const changes = view.changes;
  const total = changes?.changedParts.reduce((sum, p) => sum + p.count, 0) ?? 0;
  const screen = b.selection.screen;
  const pageChanges = screen ? null : b.publishing.changes;
  // Экран «Блог»/«Каталог»: черновики мест этого экрана (QA Р3а D7) — пилюля открывает окно публикации.
  const screenSlots = screen ? b.publishing.others.filter((o) => o.kind === "SLOT" && SITE_SLOTS.some((s) => s.key === o.slot && s.screen === screen)) : [];
  const pill = pageChanges ? pagePillOf(pageChanges) : slotsPillOf(screenSlots);
  const onPill = pageChanges ? b.openPublications : () => view.openDialog("publish");

  return (
    <span className="flex flex-wrap items-center gap-1.5">
      {hasDraftChanges(changes) ? (
        <button type="button" onClick={() => view.openDialog("changes")} className="inline-flex items-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring max-lg:min-h-12">
          <StatusPill tone="attention">
            {/* 1024–1279 — только число и стрелка, слова — для скринридера (QA SB120 3б). */}
            <span className={NARROW_HIDDEN}>Вид сайта: </span>
            {total}
            <span className={NARROW_HIDDEN}> {pluralRu(total, ["изменение", "изменения", "изменений"])}</span>
            <ChevronRight aria-hidden className="size-3" />
          </StatusPill>
        </button>
      ) : view.draft ? (
        <StatusPill tone="success">
          <Check aria-hidden className="hidden size-3 lg:max-xl:block" />
          <span className={NARROW_HIDDEN}>Вид сайта опубликован</span>
        </StatusPill>
      ) : null}
      {pill ? (
        <button
          type="button"
          onClick={onPill}
          className="inline-flex items-center rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring max-lg:min-h-12"
          aria-label={`${pill.text} — ${pageChanges ? "публикации" : "опубликовать"}`}
        >
          <StatusPill tone={pill.tone}>
            <span className={NARROW_HIDDEN}>{pill.text}</span>
            {pill.edits > 0 ? <span className="hidden lg:max-xl:inline">{pill.edits}</span> : null}
            <ChevronRight aria-hidden className="size-3" />
          </StatusPill>
        </button>
      ) : null}
    </span>
  );
}
